import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { EnterpriseEventService } from './enterprise-events/enterprise-event.service';
import { AuthEventType, AuthEventSource, AuthEventRepo } from '../enums/event/event.enum';
import { BaseEventData, FieldError, FieldInteraction } from '../models/event-data.model';

export type SiteSection = 'auth' | 'hub' | 'offers' | 'csw';

export interface AuthEnterpriseEvent {
  eventName?: string;
  subject?: string;
  signinsessionid?: string;
  parentsubject?: string;
  cid?: string;
  cpid?: string;
  article?: any;
  viewKnowledgeBaseArticle?: any;
  customerUserName?: string;
  loanleadid?: string;
  selectedContactMethodType?: string;
  selectedContactMethodValue?: string;
  utmMedium?: string;
  utmSource?: string;
  utmCampaign?: string;
  secondLoanOfferType?: string;
  marketingChannelName?: string;
  siteSection?: SiteSection;
  authClickLinkText?: string;
  authClickLinkType?: string;
  crossBuyOfferType?: string;
  eventRepo?: string;
  customerPortalDisplayText?: string;
  product?: string;
  subProduct?: string;
  customerPortalDisplayType?: string;
  customerPortalClickLinkText?: string;
  customerPortalClickLinkType?: string;
  customerPortalDisclosureText?: string;
  optInDisplayType?: string;
  optimizelyExperimentNames?: string[];
  optimizelyVariationNames?: string[];
  campaignParticipantCode?: string;
}

@Injectable({
  providedIn: 'root',
})
export class EventTrackingService {
  constructor(
    private readonly enterpriseEventService: EnterpriseEventService,
    private readonly cookieService: CookieService
  ) {}

  trackEvent<EventData extends BaseEventData>(eventType: AuthEventType, eventData: EventData) {
    let sessionId =
      this.cookieService.get('signin-session-id') || this.getStringOrDefault('signinsessionid', eventData);
    let subject = sessionId || this.cookieService.get('prospect__id');

    if (!subject) {
      console.log('unable to get suitable event subject');
      return;
    }

    const type = 'eventRepo' in eventData && eventData.eventRepo ? eventData.eventRepo : AuthEventRepo.Auth;
    eventData?.eventRepo && delete eventData.eventRepo;

    const payload = {
      type: `com.bestegg.${type}.${eventType}`,
      source: AuthEventSource.AuthFrontend,
      subject: subject,
      parentrequired: false,
      cid: this.cookieService.get('cid') || null,
      ...eventData,
    };

    Object.keys(payload).forEach((key) => {
      if (payload[key] === null || payload[key] === undefined || payload[key] === '') {
        delete payload[key];
      }
    });

    return this.enterpriseEventService.dispatchEnterpriseEvent(payload);
  }

  trackFormFieldEvent(eventData: FieldInteraction, fieldError: string = '') {
    if (!fieldError) {
      this.trackEvent<FieldInteraction>(AuthEventType.FIELD_INTERACTION, eventData);
    } else {
      this.trackEvent<FieldError>(AuthEventType.FIELD_ERROR, {
        ...eventData,
        authFormFieldErrorMessage: fieldError,
      });
    }
  }

  getStringOrDefault(key: string, data: any) {
    if (key in data && typeof data[key] === 'string') {
      const val = data[key];
      delete data[key];
      return val;
    }
    return '';
  }

  initiateAuthSession(event: AuthEnterpriseEvent) {
    const eventPayload = {
      type: `com.bestegg.auth.${event.eventName}`,
      source: `auth-frontend`,
      cid: event.cid,
      parentrequired: false,
      subject: event.signinsessionid,
      signinsessionid: event.signinsessionid,
      customerUserName: event.customerUserName,
    };
    this.enterpriseEventService.dispatchEnterpriseEvent(eventPayload);
  }

  // TECH DEBT: This fn is almost the same as trackEvent fn... we should be using one or the other, not both.
  trackEnterpriseEvent(event: AuthEnterpriseEvent) {
    if (!this.cookieService.get('signin-session-id')) {
      console.log('missing signin-session-id');
      return;
    }
    if (!event.signinsessionid) {
      event.signinsessionid = this.cookieService.get('signin-session-id');
    }

    const eventRepo = event.eventRepo || `auth`;
    const eventPayload = {
      type: `com.bestegg.${eventRepo}.${event.eventName}`,
      source: `auth-frontend`,
      subject: event.signinsessionid,
      parentrequired: false,
      cid: event.cid || this.cookieService.get('cid') || null,
      campaignParticipantCode: event.cpid,
      signinsessionid: event.signinsessionid,
      viewKnowledgeBaseArticle: event.article,
      loanleadid: event.loanleadid,
      utmMedium: event.utmMedium,
      utmSource: event.utmSource,
      utmCampaign: event.utmCampaign,
      secondLoanOfferType: event.secondLoanOfferType,
      authClickLinkText: event.authClickLinkText,
      authClickLinkType: event.authClickLinkType,
      crossBuyOfferType: event.crossBuyOfferType,
      siteSection: event.siteSection,
      customerPortalDisplayText: event.customerPortalDisplayText,
      customerPortalClickLinkText: event.customerPortalClickLinkText,
      customerPortalClickLinkType: event.customerPortalClickLinkType,
      customerPortalDisclosureText: event.customerPortalDisclosureText,
      optInDisplayType: event.optInDisplayType,
      product: event.product,
      subProduct: event.subProduct,
      customerPortalDisplayType: event.customerPortalDisplayType,
    };

    for (const key of Object.keys(eventPayload)) {
      if (eventPayload[key] === null || eventPayload[key] === undefined || eventPayload[key] === '') {
        delete eventPayload[key];
      }
    }
    return this.enterpriseEventService.dispatchEnterpriseEvent(eventPayload);
  }

  trackNonSignInEvent(event: AuthEnterpriseEvent) {
    const eventPayload = {
      type: `com.bestegg.auth.${event.eventName}`,
      source: `auth-frontend`,
      customerUserName: event.customerUserName,
      selectedContactMethodType: event?.selectedContactMethodType,
      selectedContactMethodValue: event?.selectedContactMethodValue,
      subject: event.customerUserName,
      parentrequired: false,
      marketingChannelName: event.secondLoanOfferType,
    };

    this.enterpriseEventService.dispatchEnterpriseEvent(eventPayload);
  }

  trackPageViewEvent(event: AuthEnterpriseEvent) {
    const eventRepo = event.eventRepo ? `customerPortal` : `auth`;
    const experience = this.cookieService.get('experience');
    const newWindow = window as any;
    let experienceJson;
    if (experience) {
      experienceJson = JSON.parse(experience);
    }

    const siteSection = event.siteSection || 'hub';
    const eventPayload = {
      type: `com.bestegg.${eventRepo}.${event.eventName}`,
      source: `auth-frontend`,
      subject: this.cookieService.get('signin-session-id')
        ? this.cookieService.get('signin-session-id')
        : this.cookieService.get('prospect__id'),
      parentrequired: false,
      campaignParticipantCode: experienceJson?.cpid,
      siteSection: siteSection,
      cid: this.cookieService.get('cid') || null,
      optimizelyExperimentNames: newWindow.utag_data?.optimizely_experiment_names || undefined,
      optimizelyVariationNames: newWindow.utag_data?.optimizely_variation_names || undefined,
    };

    if (!eventPayload.cid) {
      delete eventPayload.cid;
    }

    this.enterpriseEventService.dispatchEnterpriseEvent(eventPayload);
  }
}
